/**
 * Account Assets tab - list, add, edit, delete assets for an account.
 * Overview table shows first 8 fields; full detail in View modal. Dropdowns from Settings → Assets Module.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  DriveFileMove as MoveAssetIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import MoveToAccountDialog from './MoveToAccountDialog';
import { AccountAsset, AccountAssetCreate, AccountAssetUpdate } from '../types/account';
import { getApiErrorMessage } from '../utils/apiError';

interface AssetFieldOptions {
  asset_type: { value: string; label: string; sort_order: number }[];
  manufacturer: { value: string; label: string; sort_order: number }[];
  status: { value: string; label: string; sort_order: number }[];
  distributor: { value: string; label: string; sort_order: number }[];
}

interface AccountAssetsTabProps {
  accountId: string;
  onError?: (msg: string) => void;
  /** From URL `?asset=` — opens view dialog once, then call onOpenAssetConsumed */
  openAssetIdFromQuery?: string | null;
  onOpenAssetConsumed?: () => void;
}

function optionLabel(
  options: AssetFieldOptions | null,
  field: keyof AssetFieldOptions,
  value: string | null | undefined
): string {
  if (value == null || value === '') return '—';
  if (!options) return value;
  const opt = options[field].find((o) => o.value === value);
  return opt?.label ?? value;
}

/** Asset type option value from Settings → Assets Module is `router` */
function isRouterAssetType(assetType: string | null | undefined): boolean {
  return (assetType ?? '').toLowerCase() === 'router';
}

const TAUC_PORTAL_URL = 'https://tauc.tplinkcloud.com/';

const AccountAssetsTab: React.FC<AccountAssetsTabProps> = ({
  accountId,
  onError,
  openAssetIdFromQuery,
  onOpenAssetConsumed,
}) => {
  const [assets, setAssets] = useState<AccountAsset[]>([]);
  const [options, setOptions] = useState<AssetFieldOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewAsset, setViewAsset] = useState<AccountAsset | null>(null);
  const [editingAsset, setEditingAsset] = useState<AccountAsset | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [assetToMove, setAssetToMove] = useState<AccountAsset | null>(null);
  const [transferSuccess, setTransferSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<AccountAssetCreate>({
    account_id: accountId,
    asset_name: '',
    asset_type: null,
    manufacturer: null,
    model_number: '',
    status: null,
    serial_number: '',
    mac_address: '',
    distributor: null,
    sonic_warranty_item: false,
    distributor_reference: null,
    purchase_date: null,
    manufacturer_warranty_expiry: null,
    extended_warranty_expiry: null,
    notes: null,
    portal_ip_address: null,
    portal_user: null,
    portal_password: null,
    wifi_ssid: null,
    wifi_password: null,
    network_band_24ghz: false,
    network_band_5ghz: false,
    network_band_6ghz: false,
    tplink_tauc_managed: false,
  });

  const fetchAssets = async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getAccountAssets(accountId);
      setAssets(res.assets ?? []);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Failed to load assets';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const grouped = await apiService.getAssetFieldOptionsGrouped();
      setOptions(grouped);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || 'Failed to load dropdown options';
      setError(msg);
      if (onError) onError(msg);
    } finally {
      setOptionsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [accountId]);

  useEffect(() => {
    if (!openAssetIdFromQuery || loading || assets.length === 0) return;
    const a = assets.find((x) => x.id === openAssetIdFromQuery);
    if (a) {
      setViewAsset(a);
      onOpenAssetConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when query id or list loads
  }, [openAssetIdFromQuery, assets, loading]);

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    setForm((f) => ({ ...f, account_id: accountId }));
  }, [accountId]);

  const handleOpenView = (asset: AccountAsset) => {
    setViewAsset(asset);
  };

  const handleOpenAdd = () => {
    setEditingAsset(null);
    setForm({
      account_id: accountId,
      asset_name: '',
      asset_type: null,
      manufacturer: null,
      model_number: '',
      status: null,
      serial_number: '',
      mac_address: '',
      distributor: null,
      sonic_warranty_item: false,
      distributor_reference: null,
      purchase_date: null,
      manufacturer_warranty_expiry: null,
      extended_warranty_expiry: null,
      notes: null,
      portal_ip_address: null,
      portal_user: null,
      portal_password: null,
      wifi_ssid: null,
      wifi_password: null,
      network_band_24ghz: false,
      network_band_5ghz: false,
      network_band_6ghz: false,
      tplink_tauc_managed: false,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (asset: AccountAsset) => {
    setEditingAsset(asset);
    setForm({
      account_id: accountId,
      asset_name: asset.asset_name,
      asset_type: asset.asset_type ?? null,
      manufacturer: asset.manufacturer ?? null,
      model_number: asset.model_number ?? '',
      status: asset.status ?? null,
      serial_number: asset.serial_number ?? '',
      mac_address: asset.mac_address ?? '',
      distributor: asset.distributor ?? null,
      sonic_warranty_item: asset.sonic_warranty_item ?? false,
      distributor_reference: asset.distributor_reference ?? null,
      purchase_date: asset.purchase_date ?? null,
      manufacturer_warranty_expiry: asset.manufacturer_warranty_expiry ?? null,
      extended_warranty_expiry: asset.extended_warranty_expiry ?? null,
      notes: asset.notes ?? null,
      portal_ip_address: asset.portal_ip_address ?? null,
      portal_user: asset.portal_user ?? null,
      portal_password: asset.portal_password ?? null,
      wifi_ssid: asset.wifi_ssid ?? null,
      wifi_password: asset.wifi_password ?? null,
      network_band_24ghz: asset.network_band_24ghz ?? false,
      network_band_5ghz: asset.network_band_5ghz ?? false,
      network_band_6ghz: asset.network_band_6ghz ?? false,
      tplink_tauc_managed: asset.tplink_tauc_managed ?? false,
    });
    setDialogOpen(true);
  };

  const routerFieldsPayload = () => {
    if (!isRouterAssetType(form.asset_type)) {
      return {
        portal_ip_address: null,
        portal_user: null,
        portal_password: null,
        wifi_ssid: null,
        wifi_password: null,
        network_band_24ghz: false,
        network_band_5ghz: false,
        network_band_6ghz: false,
        tplink_tauc_managed: false,
      };
    }
    return {
      portal_ip_address: form.portal_ip_address?.trim() || null,
      portal_user: form.portal_user?.trim() || null,
      portal_password: form.portal_password?.trim() || null,
      wifi_ssid: form.wifi_ssid?.trim() || null,
      wifi_password: form.wifi_password?.trim() || null,
      network_band_24ghz: form.network_band_24ghz ?? false,
      network_band_5ghz: form.network_band_5ghz ?? false,
      network_band_6ghz: form.network_band_6ghz ?? false,
      tplink_tauc_managed: form.tplink_tauc_managed ?? false,
    };
  };

  const handleSave = async () => {
    const trimmedName = form.asset_name.trim();
    if (!trimmedName) {
      setError('Asset name is required');
      return;
    }
    if (!accountId?.trim()) {
      setError('Account is not loaded yet — try again in a moment.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      if (editingAsset) {
        const update: AccountAssetUpdate = {
          asset_name: trimmedName,
          asset_type: form.asset_type || null,
          manufacturer: form.manufacturer || null,
          model_number: form.model_number?.trim() || null,
          status: form.status || null,
          serial_number: form.serial_number?.trim() || null,
          mac_address: form.mac_address?.trim() || null,
          distributor: form.distributor || null,
          sonic_warranty_item: form.sonic_warranty_item ?? false,
          distributor_reference: form.distributor_reference?.trim() || null,
          purchase_date: form.purchase_date || null,
          manufacturer_warranty_expiry: form.manufacturer_warranty_expiry || null,
          extended_warranty_expiry: form.extended_warranty_expiry || null,
          notes: form.notes?.trim() || null,
          ...routerFieldsPayload(),
        };
        await apiService.updateAccountAsset(accountId, editingAsset.id, update);
      } else {
        const idForApi = accountId.trim();
        const createBody: AccountAssetCreate = {
          account_id: idForApi,
          asset_name: trimmedName,
          asset_type: form.asset_type ?? null,
          manufacturer: form.manufacturer ?? null,
          model_number: form.model_number?.trim() || null,
          status: form.status ?? null,
          serial_number: form.serial_number?.trim() || null,
          mac_address: form.mac_address?.trim() || null,
          distributor: form.distributor ?? null,
          sonic_warranty_item: form.sonic_warranty_item ?? false,
          distributor_reference: form.distributor_reference?.trim() || null,
          purchase_date: form.purchase_date ?? null,
          manufacturer_warranty_expiry: form.manufacturer_warranty_expiry ?? null,
          extended_warranty_expiry: form.extended_warranty_expiry ?? null,
          notes: form.notes?.trim() || null,
          ...routerFieldsPayload(),
        };
        await apiService.createAccountAsset(idForApi, createBody);
      }
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Failed to save asset'));
      return;
    }
    try {
      await fetchAssets();
      setDialogOpen(false);
    } catch (e: unknown) {
      setError(
        getApiErrorMessage(e, 'Asset was saved but the list could not be refreshed. Try reloading the page.'),
      );
      setDialogOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (asset: AccountAsset) => {
    if (!window.confirm(`Delete asset "${asset.asset_name}"?`)) return;
    try {
      setError(null);
      await apiService.deleteAccountAsset(accountId, asset.id);
      await fetchAssets();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const optionsReady = options && !optionsLoading;
  const dropdown = (field: keyof AssetFieldOptions, value: string | null, label: string) => {
    const list = optionsReady ? options[field] : [];
    return (
      <FormControl fullWidth size="small" margin="normal">
        <InputLabel>{label}</InputLabel>
        <Select
          value={value ?? ''}
          label={label}
          onChange={(e) => setForm({ ...form, [field]: e.target.value || null })}
        >
          <MenuItem value="">—</MenuItem>
          {list.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderViewRow = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 0.5, py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {transferSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTransferSuccess(null)}>
          {transferSuccess}
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Manage assets for this account. Overview shows key fields; use the view icon for full details. Dropdown options
          are in Settings → Assets Module.
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={handleOpenAdd}
          disabled={!accountId?.trim()}
        >
          Add asset
        </Button>
      </Box>
      <Paper variant="outlined">
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Serial</TableCell>
                  <TableCell>MAC address</TableCell>
                  <TableCell>Distributor</TableCell>
                  <TableCell align="right" width={180}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }} color="text.secondary">
                      No assets yet. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  assets.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.asset_name}</TableCell>
                      <TableCell>{optionLabel(options, 'asset_type', row.asset_type)}</TableCell>
                      <TableCell>{optionLabel(options, 'manufacturer', row.manufacturer)}</TableCell>
                      <TableCell>{row.model_number ?? '—'}</TableCell>
                      <TableCell>{optionLabel(options, 'status', row.status)}</TableCell>
                      <TableCell>{row.serial_number ?? '—'}</TableCell>
                      <TableCell>{row.mac_address ?? '—'}</TableCell>
                      <TableCell>{optionLabel(options, 'distributor', row.distributor)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenView(row)} title="View details">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAssetToMove(row);
                            setMoveOpen(true);
                          }}
                          title="Move to another account"
                        >
                          <MoveAssetIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenEdit(row)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(row)} title="Delete" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={!!viewAsset} onClose={() => setViewAsset(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Asset details</DialogTitle>
        <DialogContent dividers>
          {viewAsset && (
            <Box>
              {renderViewRow('Asset name', viewAsset.asset_name)}
              {renderViewRow('Asset type', optionLabel(options, 'asset_type', viewAsset.asset_type))}
              {renderViewRow('Manufacturer', optionLabel(options, 'manufacturer', viewAsset.manufacturer))}
              {renderViewRow('Model number', viewAsset.model_number ?? '—')}
              {renderViewRow('Status', optionLabel(options, 'status', viewAsset.status))}
              {renderViewRow('Serial number', viewAsset.serial_number ?? '—')}
              {renderViewRow('MAC address', viewAsset.mac_address ?? '—')}
              {renderViewRow('Distributor', optionLabel(options, 'distributor', viewAsset.distributor))}
              {isRouterAssetType(viewAsset.asset_type) && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 0.5, pb: 0.5 }}>
                    Router
                  </Typography>
                  {renderViewRow('Portal IP address', viewAsset.portal_ip_address ?? '—')}
                  {renderViewRow('Portal user', viewAsset.portal_user ?? '—')}
                  {renderViewRow('Portal password', viewAsset.portal_password ?? '—')}
                  {renderViewRow('WiFi SSID', viewAsset.wifi_ssid ?? '—')}
                  {renderViewRow('WiFi password', viewAsset.wifi_password ?? '—')}
                  {renderViewRow(
                    'Network',
                    [
                      viewAsset.network_band_24ghz ? '2.4 GHz' : null,
                      viewAsset.network_band_5ghz ? '5 GHz' : null,
                      viewAsset.network_band_6ghz ? '6 GHz' : null,
                    ]
                      .filter(Boolean)
                      .join(', ') || '—'
                  )}
                  {viewAsset.tplink_tauc_managed && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: 0.5, py: 0.75 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        TP-Link TAUC
                      </Typography>
                      <Typography variant="body2" component="div">
                        <Link href={TAUC_PORTAL_URL} target="_blank" rel="noopener noreferrer">
                          {TAUC_PORTAL_URL}
                        </Link>
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              <Divider sx={{ my: 1 }} />
              {renderViewRow('Sonic warranty item', viewAsset.sonic_warranty_item ? 'Yes' : 'No')}
              {renderViewRow('Distributor reference', viewAsset.distributor_reference ?? '—')}
              {renderViewRow('Purchase date', viewAsset.purchase_date ?? '—')}
              {renderViewRow('Manufacturer warranty expiry', viewAsset.manufacturer_warranty_expiry ?? '—')}
              {renderViewRow('Extended warranty expiry', viewAsset.extended_warranty_expiry ?? '—')}
              <Divider sx={{ my: 1 }} />
              {renderViewRow('Notes', viewAsset.notes?.trim() ? viewAsset.notes : '—')}
              {(viewAsset.created_at || viewAsset.updated_at) && (
                <>
                  <Divider sx={{ my: 1 }} />
                  {renderViewRow('Created', viewAsset.created_at ?? '—')}
                  {renderViewRow('Last updated', viewAsset.updated_at ?? '—')}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAsset(null)}>Close</Button>
          {viewAsset && (
            <>
              <Button
                onClick={() => {
                  const a = viewAsset;
                  setViewAsset(null);
                  setAssetToMove(a);
                  setMoveOpen(true);
                }}
              >
                Move to account
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  const a = viewAsset;
                  setViewAsset(null);
                  handleOpenEdit(a);
                }}
              >
                Edit
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <MoveToAccountDialog
        open={moveOpen}
        onClose={() => {
          setMoveOpen(false);
          setAssetToMove(null);
        }}
        excludeAccountId={accountId}
        title="Move asset to another account"
        subtitle={assetToMove ? `Asset: ${assetToMove.asset_name}` : undefined}
        onTransferred={async (targetAccountId) => {
          if (!assetToMove || !accountId) return;
          await apiService.transferAccountAsset(accountId, assetToMove.id, targetAccountId);
          await fetchAssets();
          setTransferSuccess('Asset moved to the selected account.');
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAsset ? 'Edit asset' : 'Add asset'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Asset name"
            value={form.asset_name}
            onChange={(e) => setForm({ ...form, asset_name: e.target.value })}
            fullWidth
            required
            margin="normal"
          />
          {dropdown('asset_type', form.asset_type ?? null, 'Asset type')}
          {dropdown('manufacturer', form.manufacturer ?? null, 'Manufacturer')}
          <TextField
            label="Model number"
            value={form.model_number ?? ''}
            onChange={(e) => setForm({ ...form, model_number: e.target.value })}
            fullWidth
            margin="normal"
          />
          {dropdown('status', form.status ?? null, 'Status')}
          <TextField
            label="Serial number"
            value={form.serial_number ?? ''}
            onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
            fullWidth
            margin="normal"
          />
          <TextField
            label="MAC address"
            value={form.mac_address ?? ''}
            onChange={(e) => setForm({ ...form, mac_address: e.target.value })}
            fullWidth
            margin="normal"
            placeholder="e.g. 24:9A:D8:12:6E:B6"
          />
          {dropdown('distributor', form.distributor ?? null, 'Distributor')}
          {isRouterAssetType(form.asset_type) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Router
              </Typography>
              <TextField
                label="Portal IP address"
                value={form.portal_ip_address ?? ''}
                onChange={(e) => setForm({ ...form, portal_ip_address: e.target.value || null })}
                fullWidth
                margin="normal"
                placeholder="e.g. 192.168.0.1"
              />
              <TextField
                label="Portal user"
                value={form.portal_user ?? ''}
                onChange={(e) => setForm({ ...form, portal_user: e.target.value || null })}
                fullWidth
                margin="normal"
                autoComplete="off"
              />
              <TextField
                label="Portal password"
                type="password"
                value={form.portal_password ?? ''}
                onChange={(e) => setForm({ ...form, portal_password: e.target.value || null })}
                fullWidth
                margin="normal"
                autoComplete="new-password"
              />
              <TextField
                label="WiFi SSID"
                value={form.wifi_ssid ?? ''}
                onChange={(e) => setForm({ ...form, wifi_ssid: e.target.value || null })}
                fullWidth
                margin="normal"
                autoComplete="off"
              />
              <TextField
                label="WiFi password"
                type="password"
                value={form.wifi_password ?? ''}
                onChange={(e) => setForm({ ...form, wifi_password: e.target.value || null })}
                fullWidth
                margin="normal"
                autoComplete="new-password"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                Network
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', pl: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.network_band_24ghz ?? false}
                      onChange={(e) => setForm({ ...form, network_band_24ghz: e.target.checked })}
                    />
                  }
                  label="2.4 GHz"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.network_band_5ghz ?? false}
                      onChange={(e) => setForm({ ...form, network_band_5ghz: e.target.checked })}
                    />
                  }
                  label="5 GHz"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.network_band_6ghz ?? false}
                      onChange={(e) => setForm({ ...form, network_band_6ghz: e.target.checked })}
                    />
                  }
                  label="6 GHz"
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.tplink_tauc_managed ?? false}
                    onChange={(e) => setForm({ ...form, tplink_tauc_managed: e.target.checked })}
                  />
                }
                label="TP-Link TAUC (managed via TAUC portal)"
                sx={{ display: 'block', mt: 1 }}
              />
            </>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={form.sonic_warranty_item ?? false}
                onChange={(e) => setForm({ ...form, sonic_warranty_item: e.target.checked })}
              />
            }
            label="Sonic warranty item"
            sx={{ mt: 1, display: 'block' }}
          />
          <TextField
            label="Distributor reference"
            value={form.distributor_reference ?? ''}
            onChange={(e) => setForm({ ...form, distributor_reference: e.target.value })}
            fullWidth
            margin="normal"
            placeholder="Distributor reference number or code"
          />
          <TextField
            label="Purchase date"
            type="date"
            value={form.purchase_date ?? ''}
            onChange={(e) => setForm({ ...form, purchase_date: e.target.value || null })}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Manufacturer warranty expiry"
            type="date"
            value={form.manufacturer_warranty_expiry ?? ''}
            onChange={(e) => setForm({ ...form, manufacturer_warranty_expiry: e.target.value || null })}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Extended warranty expiry"
            type="date"
            value={form.extended_warranty_expiry ?? ''}
            onChange={(e) => setForm({ ...form, extended_warranty_expiry: e.target.value || null })}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Notes"
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
            fullWidth
            margin="normal"
            multiline
            minRows={3}
            placeholder="Optional notes about this asset"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountAssetsTab;
